/**
 * Kanban (spec page 4, approved): grouped columns Intake / Development /
 * Test + Release / Close-out / Done. Cards carry their exact sub-stage tag.
 * Click a group header to expand it into sub-stage columns. Drag a card onto
 * a group (-> that group's first stage, unless it's already in the group) or
 * onto an expanded sub-stage column (-> exactly that stage). Done shows a
 * count and accepts drops (-> Completed).
 */
import { useState } from 'react';
import {
  DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import { STAGE_GROUPS, STATUSES, type Settings, type StageGroup, type Status } from '../config';
import { updateRequest, type Me, type Req } from '../lib/data';
import { lineupOrder } from '../lib/order';
import { applyFilter, Card, Filters, NO_FILTER, type Filter } from '../parts';
import { Guide } from '../guide';

const groupOf = (s: string) => STATUSES.find((x) => x.id === s)?.group ?? 'intake';

export function Kanban({ me, items, settings, go, initial }: {
  me: Me; items: Req[]; settings: Settings; go: (to: string) => void; initial?: Partial<Filter>;
}) {
  const [filter, setFilter] = useState<Filter>({ ...NO_FILTER, ...initial });
  const [expanded, setExpanded] = useState<StageGroup | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const shown = applyFilter(items, filter);
  const doneCount = shown.filter((r) => r.status === 'completed').length;

  async function onDragEnd(e: DragEndEvent) {
    const r = items.find((x) => x.id === e.active.id);
    const target = e.over?.id as string | undefined;
    if (!r || !target) return;
    let status: Status | null = null;
    if (target.startsWith('status:')) status = target.slice(7) as Status;
    else if (target.startsWith('group:')) {
      const g = target.slice(6) as StageGroup;
      if (groupOf(r.status) !== g) status = STATUSES.find((s) => s.group === g)!.id;
    }
    if (!status || status === r.status) return;
    setErr(null);
    try { await updateRequest(me, r, { status }); } catch (x) { setErr((x as Error).message); }
  }

  return (
    <div>
      <div className="toolbar">
        <h2 className="page-title">Kanban</h2>
        <Filters items={items} value={filter} onChange={setFilter} />
        <span className="spacer" />
        <span className="muted small">Click a column title to expand its stages. Drag cards to move them.</span>
      </div>
      <Guide page="kanban" />
      {err && <p className="err">{err}</p>}
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="board">
          {STAGE_GROUPS.map((g) => {
            if (g.id === 'done') {
              return (
                <DropCol key={g.id} id="group:done" className="col done-col">
                  <div className="col-head"><span>Done</span><span className="count">{doneCount}</span></div>
                  <p className="muted small">Drop here to complete.</p>
                  <button className="linkish" onClick={() => go('/done')}>View completed</button>
                </DropCol>
              );
            }
            const inGroup = lineupOrder(shown.filter((r) => groupOf(r.status) === g.id));
            if (expanded === g.id) {
              const stages = STATUSES.filter((s) => s.group === g.id);
              return (
                <div key={g.id} className="col-group expanded">
                  <div className="col-head group-head" onClick={() => setExpanded(null)} title="Collapse">
                    <span>{g.label}</span><span className="count">{inGroup.length}</span><span className="muted small">collapse</span>
                  </div>
                  <div className="subcols">
                    {stages.map((s) => {
                      const cards = inGroup.filter((r) => r.status === s.id);
                      return (
                        <DropCol key={s.id} id={`status:${s.id}`} className="col sub">
                          <div className="col-head sub-head"><span>{s.label.replace('Dev: ', '')}</span><span className="count">{cards.length}</span></div>
                          {cards.map((r) => <Drag key={r.id} id={r.id}><Card r={r} settings={settings} onOpen={(id) => go(`/r/${id}`)} showStatus={false} /></Drag>)}
                        </DropCol>
                      );
                    })}
                  </div>
                </div>
              );
            }
            return (
              <DropCol key={g.id} id={`group:${g.id}`} className="col">
                <div className="col-head group-head" onClick={() => setExpanded(g.id)} title="Expand into stages">
                  <span>{g.label}</span><span className="count">{inGroup.length}</span>
                </div>
                {inGroup.map((r) => <Drag key={r.id} id={r.id}><Card r={r} settings={settings} onOpen={(id) => go(`/r/${id}`)} /></Drag>)}
              </DropCol>
            );
          })}
        </div>
      </DndContext>
    </div>
  );
}

function DropCol({ id, className, children }: { id: string; className: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return <div ref={setNodeRef} className={`${className} ${isOver ? 'over' : ''}`}>{children}</div>;
}

function Drag({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 } : undefined;
  return <div ref={setNodeRef} style={style} className={isDragging ? 'dragging' : ''} {...listeners} {...attributes}>{children}</div>;
}
